import { useCallback, useEffect, useRef, useState } from "react";
import { studio } from "@/lib/studioApi";

export const CHAT_KEY = "dv-assistant-chat";

export const useAssistantChat = () => {
  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(() => localStorage.getItem(CHAT_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const current = useRef(chatId);

  const loadChats = useCallback(async () => {
    const { data } = await studio.get("/assistant/chats");
    setChats(data.chats || []);
    return data.chats || [];
  }, []);

  const open = useCallback(async (id) => {
    current.current = id;
    setChatId(id);
    localStorage.setItem(CHAT_KEY, id);
    setLoading(true);
    setError("");
    try {
      const { data } = await studio.get(`/assistant/chats/${id}`);
      if (current.current === id) setMessages(data.messages || []);
    } catch {
      localStorage.removeItem(CHAT_KEY);
      setChatId("");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const startNew = useCallback(async () => {
    const { data } = await studio.post("/assistant/chats", {});
    setChats((c) => [data, ...c]);
    current.current = data.id;
    setChatId(data.id);
    localStorage.setItem(CHAT_KEY, data.id);
    setMessages([]);
    setError("");
    return data.id;
  }, []);

  const remove = useCallback(async (id) => {
    await studio.delete(`/assistant/chats/${id}`);
    setChats((c) => c.filter((x) => x.id !== id));
    if (current.current === id) {
      localStorage.removeItem(CHAT_KEY);
      current.current = "";
      setChatId("");
      setMessages([]);
    }
  }, []);

  const send = useCallback(async (text, answers) => {
    let id = current.current;
    if (!id) id = await startNew();
    const temp = { id: `tmp-${Date.now()}`, role: "user", content: text, kind: answers ? "answers" : "text", pending: true };
    setMessages((m) => [...m, temp]);
    setSending(true);
    setError("");
    try {
      const { data } = await studio.post(`/assistant/chats/${id}/message`, { text, answers }, { timeout: 300000 });
      setMessages((m) => [...m.filter((x) => x.id !== temp.id), data.user_message, data.reply]);
      setChats((c) => c.map((x) => (x.id === id ? { ...x, title: data.title, updated_at: new Date().toISOString() } : x)));
      return data.reply;
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      setError(e?.response?.data?.detail || "L'assistant n'a pas pu répondre. Réessayez.");
      return null;
    } finally {
      setSending(false);
    }
  }, [startNew]);

  const sendPhoto = useCallback(async (file, text) => {
    let id = current.current;
    if (!id) id = await startNew();
    const previewUrl = URL.createObjectURL(file);
    const temp = { id: `tmp-${Date.now()}`, role: "user", content: text || "Voici une photo.", kind: "photo", data: { image: previewUrl }, pending: true };
    setMessages((m) => [...m, temp]);
    setSending(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("text", text || "");
      const { data } = await studio.post(`/assistant/chats/${id}/photo`, fd, { timeout: 300000 });
      setMessages((m) => [...m.filter((x) => x.id !== temp.id), data.user_message, data.reply]);
      setChats((c) => c.map((x) => (x.id === id ? { ...x, title: data.title, updated_at: new Date().toISOString() } : x)));
      return data.reply;
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      setError(e?.response?.data?.detail || "L'assistant n'a pas pu analyser la photo. Réessayez.");
      return null;
    } finally {
      URL.revokeObjectURL(previewUrl);
      setSending(false);
    }
  }, [startNew]);

  useEffect(() => {
    loadChats().catch(() => {});
    if (chatId) open(chatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { chats, chatId, messages, loading, sending, error, open, startNew, remove, send, sendPhoto, loadChats };
};
